// TicketDocuments.tsx
import React from 'react';
import {
    Box, Typography, Paper, IconButton, Button, Skeleton
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import MicIcon from '@mui/icons-material/Mic';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

interface AttachedFile {
    file: File;
    renamedName: string;
}
type TicketDocumentsProps = {
    ticketDocuments: { file_name: string; file: string }[];
    loadingTicketDocuments: boolean;
    uploadingFiles: boolean;
    pendingTicketFiles: { renamedName: string }[];
    handleTicketFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    removePendingFile: (index: number) => void;
    handleUploadPendingTicketFiles: () => void;
    currentStatus: string;
    setPreviewFile: (file: { url: string; type: string }) => void;
    setPendingTicketFiles: (files: AttachedFile[]) => void;
};

const TicketDocuments = ({
    ticketDocuments,
    loadingTicketDocuments,
    uploadingFiles,
    pendingTicketFiles,
    handleTicketFileUpload,
    removePendingFile,
    handleUploadPendingTicketFiles,
    currentStatus,
    setPendingTicketFiles,
    setPreviewFile
}: TicketDocumentsProps) => {
    const getIcon = (ext: string) => {
        if (['jpg', 'jpeg', 'png'].includes(ext)) return <ImageIcon />;
        if (ext === 'pdf') return <PictureAsPdfIcon />;
        if (['mp3', 'wav', 'ogg'].includes(ext)) return <MicIcon />;
        return <InsertDriveFileIcon />;
    };

    return (
        <>
            <Typography fontWeight={600} mb={1}>Documentos adjuntos</Typography>
            <Box
                sx={{
                    maxHeight: 100,
                    overflowY: 'auto',
                    backgroundColor: '#F9FAFB',
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    padding: 1.5,
                    mb: 2,
                }}
            >
                {loadingTicketDocuments || uploadingFiles ? (
                    [...Array(2)].map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={50} sx={{ borderRadius: 2, mb: 1 }} />
                    ))
                ) : ticketDocuments.length > 0 ? (
                    ticketDocuments.map((file, index) => {


                        const ext = file.file_name.split('.').pop()?.toLowerCase() || '';
                        // const mimeType = ext === 'pdf' ? 'application/pdf' : ext.startsWith('mp') ? `audio/${ext}` : `image/${ext}`;
                        // const fileUrl = `data:${mimeType};base64,${file.file}`;

                        const getMimeType = (ext: string) => {
                            const imageTypes = ['jpg', 'jpeg', 'png'];
                            const audioTypes = ['mp3', 'wav', 'ogg', 'm4a'];
                            if (imageTypes.includes(ext)) return `image/${ext}`;
                            if (audioTypes.includes(ext)) return `audio/${ext}`;
                            if (ext === 'pdf') return 'application/pdf';
                            return 'application/octet-stream';
                        };

                        const mimeType = getMimeType(ext);
                        const fileUrl = `data:${mimeType};base64,${file.file}`;



                        return (
                            <Paper key={index} sx={{ p: 1, mb: 1 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {getIcon(ext)}
                                        <Typography variant="body2" noWrap maxWidth={150}>{file.file_name}</Typography>
                                    </Box>
                                    <Box display="flex" gap={1}>
                                        {(ext === 'pdf' || ['jpg', 'jpeg', 'png'].includes(ext)) && (
                                            <IconButton size="small" onClick={() => setPreviewFile({ url: fileUrl, type: mimeType })}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const link = document.createElement('a');
                                                link.href = fileUrl;
                                                link.download = file.file_name;
                                                link.click();
                                            }}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                                {['mp3', 'wav', 'ogg'].includes(ext) && (
                                    <Box mt={1}>
                                        <audio controls style={{ width: '100%' }}>
                                            <source src={fileUrl} type={mimeType} />
                                            Tu navegador no soporta la reproducción de audio.
                                        </audio>
                                    </Box>
                                )}
                            </Paper>
                        );
                    })
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No hay documentos adjuntos para este ticket.
                    </Typography>
                )}
            </Box>

            <Box width="100%">
                <input
                    id="side-doc-upload"
                    type="file"
                    multiple
                    hidden
                    onChange={handleTicketFileUpload}
                    disabled={currentStatus === "CLOSE"}
                />
                <label htmlFor="side-doc-upload" style={{ width: '100%' }}>
                    <Button
                        startIcon={<AttachFileIcon />}
                        variant="outlined"
                        disabled={currentStatus === "CLOSE"}
                        fullWidth
                        component="span"
                    >
                        Adjuntar documento
                    </Button>
                </label>
            </Box>

            {pendingTicketFiles.length > 0 && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">Archivos pendientes por subir:</Typography>
                    {pendingTicketFiles.map((f, i) => (
                        <Box key={i} display="flex" justifyContent="space-between" alignItems="center" mt={1} p={1} bgcolor="#F3F4F6" borderRadius={1}>
                            <Typography variant="body2" noWrap maxWidth="70%">{f.renamedName}</Typography>
                            <IconButton size="small" onClick={() => removePendingFile(i)}><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                    ))}
                    <Box mt={1} display="flex" gap={1}>
                        <Button fullWidth onClick={handleUploadPendingTicketFiles}>Actualizar</Button>
                        <Button fullWidth onClick={() => setPendingTicketFiles([])}>Cancelar</Button>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default TicketDocuments;



